// Config-only DS Table column for one custom field. Sorting uses an accessorFn over the typed sort value
// plus sortUndefined: 'last', so blanks stay at the bottom in both directions - see customFieldSortValue.
// Long values truncate to one line with an ellipsis, and the full value shows in a design-system hover box
// when it is cut off - see TruncatedValue.
// (Dist-internal imports are the accepted convention - see LinkColumn.)
import React from 'react';
import Styled from 'styled-components';
import type {ColumnDef} from '@tanstack/react-table';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {Tooltip} from '@forecast-it/design-system';
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import {
	compareCustomFieldSortValues,
	customFieldSortValue,
	formatCustomFieldValue,
	type CustomFieldColumnDef,
} from '../customFieldColumnModel';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import {CustomFieldTooltipText} from './CustomFieldTooltipText';
import {useIsTruncatedHeight, useIsTruncatedWidth} from './useIsTruncatedWidth';

export interface CustomFieldColumnType {
	id: string;
	header: string;
	definition: CustomFieldColumnDef;
	locale: string;
	// The viewer's decimal separator, so a number reads the way the inline editor parses it.
	decimalSeparator?: ',' | '.';
	visible?: boolean;
	width?: string;
	'data-cy'?: string;
	// Wraps the rendered value - the call site supplies the inline editor. Identity by default.
	cellContent?: (content: React.ReactNode, row: OpportunityTableRow) => React.ReactNode;
}

// Values are already typed per column; the model's comparator compares them for the column's kind (exact
// for numbers, which are kept as strings so long values do not round).
const byTypedValue = (definition: CustomFieldColumnDef): OpportunitySortingFn => {
	const compare = compareCustomFieldSortValues(definition);
	return (a, b, columnId) =>
		Math.sign(compare(a.getValue(columnId) as string | number, b.getValue(columnId) as string | number));
};

// Admin-typed names can be long; the header wraps to at most two lines and ends in an ellipsis, so one long
// name cannot make the whole header row tall.
const HeaderLabel = Styled.span`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	overflow-wrap: anywhere;
`;

// The full name shows in a design-system hover box only while the clamp cuts it off; a name that fits gets
// no hover at all. The box is interactable so a click on the label still reaches the header's sort toggle.
const HeaderName = ({name}: {name: string}): JSX.Element => {
	const [ref, isTruncated] = useIsTruncatedHeight<HTMLSpanElement>();
	const label = (
		<HeaderLabel ref={ref} data-cy="cf-header-label">
			{name}
		</HeaderLabel>
	);
	return isTruncated ? (
		<Tooltip content={<CustomFieldTooltipText>{name}</CustomFieldTooltipText>} interactable>
			{label}
		</Tooltip>
	) : (
		label
	);
};

// One line, cut with an ellipsis. The design-system cell is a nowrap flex row, so a line clamp never
// wraps (nothing to measure by height) and a flex item that cannot shrink below its text never draws an
// ellipsis. Hence nowrap + text-overflow here, min-width: 0 so this box (and the flex wrappers around it -
// the EditableCell trigger and the read-only fill) can shrink to the cell.
const Value = Styled.span`
	display: block;
	flex: 0 1 auto;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

// The full value shows in a design-system hover box, only while the value is cut off, for every user. The
// box is interactable so a click still reaches the inline editor around the value, and the anchor is the
// clipped value itself, so the box's arrow centres on the text that is shown. Whether the cell can be edited
// is the call site's native title, not this box - see customFieldCell on the page.
const TruncatedValue = ({value}: {value: string}): JSX.Element => {
	const [ref, isTruncated] = useIsTruncatedWidth<HTMLSpanElement>();
	const text = (
		<Value ref={ref} data-cy="cf-value">
			{value}
		</Value>
	);
	return isTruncated ? (
		<Tooltip content={<CustomFieldTooltipText>{value}</CustomFieldTooltipText>} interactable>
			{text}
		</Tooltip>
	) : (
		text
	);
};

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomFieldColumn = (_: CustomFieldColumnType): null => null;

CustomFieldColumn.toColumnDef = (child: React.ReactElement<CustomFieldColumnType>): ColumnDef<OpportunityTableRow> => {
	const {definition, locale, decimalSeparator, cellContent} = child.props;
	// Every kind is centred under its header, the way the list's date, stage and status columns are.
	const align = Align.center;
	// allowSorting only picks the sortable header here; TanStack enables sorting because accessorFn is set.
	const name = child.props.header;
	const baseProps: Parameters<typeof defaultColumnDef>[0] & {allowSorting: boolean} = {
		id: child.props.id,
		header: (<HeaderName name={name} />) as unknown as string,
		visible: child.props.visible,
		width: child.props.width,
		'data-cy': child.props['data-cy'],
		align,
		allowSorting: true,
	};
	const base = defaultColumnDef(baseProps);
	return {
		...base,
		// The header cell renders the clamped label above; the table labels the column (aria-label) and
		// exports it from meta.header, which must stay the plain name.
		meta: {...base.meta, header: name},
		id: child.props.id,
		accessorFn: (row: OpportunityTableRow) => customFieldSortValue(definition, row.customFieldValues[definition.key]),
		sortingFn: byTypedValue(definition),
		sortUndefined: 'last',
		cell: ({row}: {row: {original: OpportunityTableRow}}) => {
			const display = formatCustomFieldValue(
				definition,
				row.original.customFieldValues[definition.key],
				locale,
				decimalSeparator
			);
			const content = display ? <TruncatedValue value={display} /> : null;
			const cellCy = child.props['data-cy'] ? `${child.props['data-cy']}-cell` : undefined;
			return (
				<StyledCell align={align} data-cy={cellCy}>
					{cellContent ? cellContent(content, row.original) : content}
				</StyledCell>
			);
		},
	};
};

export default CustomFieldColumn;
